/**
 * Sidebar - Navigation sidebar component
 * Ink "Decision Cut" brand panel (dark in both themes) with white wordmark.
 * Provides main navigation and team switcher.
 */

import * as Popover from '@radix-ui/react-popover';
import {
  FolderKanban,
  BarChart3,
  Settings,
  ChevronDown,
  Check,
  ChevronRight,
  PieChart,
  Shield,
  Lightbulb,
  LayoutList,
  FileSpreadsheet,
} from 'lucide-react';
import * as React from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';

import { Wordmark } from '@/components/brand/Wordmark';
import { useAuth } from '@/context/AuthContext';
import { cn } from '@/lib/utils';

interface NavItem {
  label: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
}

const navItems: NavItem[] = [
  {
    label: 'Projects',
    href: '/projects',
    icon: FolderKanban,
  },
];

interface DashboardSubItem {
  label: string;
  tab: string;
  icon: React.ComponentType<{ className?: string }>;
}

const dashboardSubItems: DashboardSubItem[] = [
  { label: 'Verdicts', tab: 'verdicts', icon: PieChart },
  { label: 'Gates', tab: 'gates', icon: Shield },
  { label: 'Insights', tab: 'insights', icon: Lightbulb },
  { label: 'Creatives', tab: 'creatives', icon: LayoutList },
  { label: 'Reports', tab: 'reports', icon: FileSpreadsheet },
];

/** Shared styles for sidebar nav rows on the ink panel */
const navRowClass = (isActive: boolean): string =>
  cn(
    'relative flex h-11 items-center gap-3 rounded-md px-4 text-md font-normal transition-all duration-base ease-standard',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30',
    isActive
      ? 'bg-white/[0.08] text-sidebar-text-active'
      : 'text-sidebar-text hover:bg-white/[0.05] hover:text-sidebar-text-active'
  );

/** Cobalt cut — active indicator */
function ActiveCut(): React.ReactElement {
  return (
    <span
      className="absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-pill bg-[#2563FF]"
      aria-hidden="true"
    />
  );
}

function DashboardNavSection() {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const isDashboardActive = location.pathname === '/dashboard';
  const activeTab = searchParams.get('tab') || 'verdicts';
  const [expanded, setExpanded] = React.useState(isDashboardActive);

  React.useEffect(() => {
    if (isDashboardActive) setExpanded(true);
  }, [isDashboardActive]);

  return (
    <div>
      <button
        onClick={() => setExpanded(!expanded)}
        className={cn(navRowClass(isDashboardActive), 'w-full')}
      >
        {isDashboardActive && <ActiveCut />}
        <BarChart3 className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
        <span className="flex-1 text-left">Dashboard</span>
        <ChevronRight
          className={cn(
            'h-4 w-4 transition-transform duration-base',
            expanded && 'rotate-90'
          )}
        />
      </button>
      {expanded && (
        <div className="ml-4 mt-1 space-y-0.5">
          {dashboardSubItems.map((sub) => {
            const SubIcon = sub.icon;
            const isSubActive = isDashboardActive && activeTab === sub.tab;
            return (
              <NavLink
                key={sub.tab}
                to={`/dashboard?tab=${sub.tab}`}
                className={cn(
                  'relative flex h-9 items-center gap-2.5 rounded-md px-3 text-sm font-normal transition-all duration-base ease-standard',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30',
                  isSubActive
                    ? 'bg-white/[0.08] text-sidebar-text-active'
                    : 'text-sidebar-text hover:bg-white/[0.05] hover:text-sidebar-text-active'
                )}
              >
                {isSubActive && <ActiveCut />}
                <SubIcon className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                <span>{sub.label}</span>
              </NavLink>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function Sidebar(): React.ReactElement {
  const { currentTeam, teams, setCurrentTeam } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [teamDropdownOpen, setTeamDropdownOpen] = React.useState(false);

  // Dynamic settings path based on current team
  const teamSettingsPath = currentTeam ? `/teams/${currentTeam.teamId}/settings` : '/settings';

  const isTeamSettingsActive =
    location.pathname.includes('/teams/') && location.pathname.includes('/settings');

  return (
    <div className="flex h-full flex-col bg-sidebar">
      {/* Logo section */}
      <div className="flex h-topbar items-center border-b border-sidebar-border px-6">
        <NavLink
          to="/"
          aria-label="Adverdict home"
          className="rounded-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
        >
          <Wordmark tone="white" className="h-[18px]" />
        </NavLink>
      </div>

      {/* Navigation items */}
      <nav className="flex-1 space-y-1 p-6" aria-label="Main navigation">
        {navItems.map((item) => {
          const Icon = item.icon;
          const isActive =
            location.pathname === item.href ||
            (item.href !== '/' && location.pathname.startsWith(item.href));

          return (
            <NavLink
              key={item.href}
              to={item.href}
              className={navRowClass(isActive)}
              aria-current={isActive ? 'page' : undefined}
            >
              {isActive && <ActiveCut />}
              <Icon className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
              <span>{item.label}</span>
            </NavLink>
          );
        })}

        {/* Dashboard section with sub-items */}
        <DashboardNavSection />

        {/* Team Settings - dynamic link based on current team */}
        <NavLink
          to={teamSettingsPath}
          className={navRowClass(isTeamSettingsActive)}
          aria-current={location.pathname === teamSettingsPath ? 'page' : undefined}
        >
          {isTeamSettingsActive && <ActiveCut />}
          <Settings className="h-5 w-5 flex-shrink-0" aria-hidden="true" />
          <span>Team Settings</span>
        </NavLink>
      </nav>

      {/* Team switcher at bottom */}
      <div className="border-t border-sidebar-border p-6">
        <Popover.Root open={teamDropdownOpen} onOpenChange={setTeamDropdownOpen}>
          <Popover.Trigger asChild>
            <button
              className="flex w-full items-center justify-between rounded-md border border-sidebar-border bg-sidebar-surface p-3 text-left transition-all duration-base hover:border-white/20 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30"
              aria-label="Switch team"
            >
              <div className="flex min-w-0 flex-1 items-center gap-3">
                <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-[#2563FF]">
                  <span className="font-semibold text-xs uppercase text-white">
                    {currentTeam?.name.substring(0, 2) || '?'}
                  </span>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-sm text-sidebar-text-active">
                    {currentTeam?.name || 'No team selected'}
                  </p>
                  <p className="truncate text-xs text-sidebar-text">
                    {currentTeam?.memberCount || 0} member
                    {currentTeam?.memberCount !== 1 ? 's' : ''}
                  </p>
                </div>
              </div>
              <ChevronDown
                className="h-4 w-4 flex-shrink-0 text-sidebar-text transition-transform duration-base"
                style={{
                  transform: teamDropdownOpen ? 'rotate(180deg)' : undefined,
                }}
                aria-hidden="true"
              />
            </button>
          </Popover.Trigger>

          <Popover.Portal>
            <Popover.Content
              className="z-50 w-64 rounded-lg border border-border-subtle bg-surface p-2 shadow-md"
              sideOffset={8}
              align="start"
            >
              <div className="space-y-1">
                {teams.map((team) => {
                  const isSelected = team.teamId === currentTeam?.teamId;

                  return (
                    <div
                      key={team.teamId}
                      className={cn(
                        'flex w-full items-center gap-3 rounded-md p-2 transition-all duration-base',
                        isSelected
                          ? 'bg-brand-soft text-brand'
                          : 'text-secondary hover:bg-surface-subtle'
                      )}
                    >
                      <button
                        onClick={() => {
                          setCurrentTeam(team);
                          setTeamDropdownOpen(false);
                        }}
                        className="flex min-w-0 flex-1 items-center gap-3 text-left"
                      >
                        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-md bg-brand/10">
                          <span className="font-semibold text-xs uppercase text-brand">
                            {team.name.substring(0, 2)}
                          </span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate font-medium text-sm">
                            {team.name}
                          </p>
                          <p className="truncate text-xs opacity-70">
                            {team.role === 'admin' ? 'Admin' : 'Member'} •{' '}
                            {team.memberCount} member
                            {team.memberCount !== 1 ? 's' : ''}
                          </p>
                        </div>
                      </button>
                      <div className="flex items-center gap-1">
                        {isSelected && (
                          <Check className="h-4 w-4 text-brand" aria-label="Selected" />
                        )}
                        {team.role === 'admin' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setTeamDropdownOpen(false);
                              navigate(`/teams/${team.teamId}/settings`);
                            }}
                            className="rounded-sm p-1 hover:bg-surface-subtle"
                            title="Team Settings"
                          >
                            <Settings className="h-4 w-4 text-muted hover:text-secondary" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </Popover.Content>
          </Popover.Portal>
        </Popover.Root>
      </div>
    </div>
  );
}
